import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { useAuth } from "./AuthContext";
import { useRealtime } from "./RealtimeContext";

const ROMessagesContext = createContext(null);

const MAX_BODY_LENGTH = 4000;
const INITIAL_LIMIT = 50;

const EMPTY_THREAD = Object.freeze({
  canonicalMessages: [],
  optimisticMessages: [],
  loaded: false,
  loading: false,
  refreshing: false,
  loadError: "",
  hasMore: false,
  nextCursor: null,
});

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function threadKey(storeId, roId) {
  return `${safeString(storeId).trim()}::${safeString(roId).trim()}`;
}

function nowIso() {
  return new Date().toISOString();
}

function messageTime(message) {
  const raw = safeString(message?.createdAt || message?.localCreatedAt).trim();
  const d = raw ? new Date(raw) : null;
  return d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
}

function compareMessageIds(a, b) {
  const left = safeString(a).trim();
  const right = safeString(b).trim();

  if (/^\d+$/.test(left) && /^\d+$/.test(right)) {
    const normalizedLeft = left.replace(/^0+/, "") || "0";
    const normalizedRight = right.replace(/^0+/, "") || "0";

    if (normalizedLeft.length !== normalizedRight.length) {
      return normalizedLeft.length - normalizedRight.length;
    }

    return normalizedLeft.localeCompare(normalizedRight);
  }

  return left.localeCompare(right);
}

function compareMessages(a, b) {
  const at = messageTime(a);
  const bt = messageTime(b);

  if (at !== bt) return at - bt;

  const aid = safeString(a?.id).trim();
  const bid = safeString(b?.id).trim();
  if (aid || bid) return compareMessageIds(aid, bid);

  return safeString(a?.localId).localeCompare(safeString(b?.localId));
}

function normalizeServerMessage(input) {
  const m = input && typeof input === "object" ? input : {};
  const isRetracted = m.isRetracted === true || !!m.retractedAt;
  const retractedAt = safeString(m.retractedAt).trim();

  return {
    id: safeString(m.id).trim(),
    storeId: safeString(m.storeId).trim(),
    roId: safeString(m.roId).trim(),
    authorUserId: safeString(m.authorUserId).trim(),
    authorDisplayName: safeString(m.authorDisplayName).trim(),
    authorRole: safeString(m.authorRole).trim(),
    body: isRetracted ? "" : safeString(m.body),
    clientMessageId: safeString(m.clientMessageId).trim(),
    createdAt: safeString(m.createdAt).trim(),
    retractedAt: isRetracted ? retractedAt : null,
    isRetracted,
  };
}

function isValidServerMessage(message) {
  return !!(
    message?.id &&
    message?.storeId &&
    message?.roId &&
    message?.authorUserId &&
    message?.clientMessageId &&
    message?.createdAt
  );
}

function optimisticMatchKey(message) {
  const authorUserId = safeString(message?.authorUserId).trim();
  const clientMessageId = safeString(message?.clientMessageId).trim();
  return authorUserId && clientMessageId
    ? `${authorUserId}::${clientMessageId}`
    : "";
}

function localIntentMatchesCanonical(intent, message) {
  return !!(
    safeString(intent?.storeId).trim() &&
    safeString(intent?.roId).trim() &&
    safeString(intent?.authorUserId).trim() &&
    safeString(intent?.clientMessageId).trim() &&
    safeString(intent.storeId).trim() === safeString(message?.storeId).trim() &&
    safeString(intent.roId).trim() === safeString(message?.roId).trim() &&
    safeString(intent.authorUserId).trim() ===
      safeString(message?.authorUserId).trim() &&
    safeString(intent.clientMessageId).trim() ===
      safeString(message?.clientMessageId).trim()
  );
}

function getThread(state, key) {
  return state.threads[key] || EMPTY_THREAD;
}

function mergeCanonicalMessages(existing, incoming) {
  const byId = new Map();

  (Array.isArray(existing) ? existing : []).forEach((message) => {
    const normalized = normalizeServerMessage(message);
    if (normalized.id) byId.set(normalized.id, normalized);
  });

  (Array.isArray(incoming) ? incoming : []).forEach((message) => {
    const normalized = normalizeServerMessage(message);
    if (!isValidServerMessage(normalized)) return;

    const previous = byId.get(normalized.id);
    if (previous?.isRetracted && !normalized.isRetracted) {
      byId.set(normalized.id, {
        ...previous,
        ...normalized,
        body: "",
        retractedAt: previous.retractedAt,
        isRetracted: true,
      });
      return;
    }

    if (normalized.isRetracted) {
      byId.set(normalized.id, {
        ...(previous || {}),
        ...normalized,
        body: "",
        retractedAt: normalized.retractedAt || previous?.retractedAt || "",
        isRetracted: true,
      });
      return;
    }

    byId.set(normalized.id, normalized);
  });

  return Array.from(byId.values()).sort(compareMessages);
}

function removeConfirmedOptimistic(optimisticMessages, canonicalMessages) {
  const confirmedKeys = new Set(
    canonicalMessages.map(optimisticMatchKey).filter(Boolean)
  );

  return (Array.isArray(optimisticMessages) ? optimisticMessages : []).filter(
    (message) => !confirmedKeys.has(optimisticMatchKey(message))
  );
}

function visibleMessages(thread) {
  const canonical = (thread.canonicalMessages || []).map((message) => ({
    ...message,
    localId: "",
    status: "sent",
    error: "",
    retryable: false,
  }));

  const canonicalKeys = new Set(canonical.map(optimisticMatchKey).filter(Boolean));

  const optimistic = (thread.optimisticMessages || []).filter(
    (message) => !canonicalKeys.has(optimisticMatchKey(message))
  );

  return [...canonical, ...optimistic].sort(compareMessages);
}

function reducer(state, action) {
  switch (action.type) {
    case "RESET":
      return { threads: {} };

    case "LOAD_START": {
      const thread = getThread(state, action.key);
      return {
        ...state,
        threads: {
          ...state.threads,
          [action.key]: {
            ...thread,
            loading: !thread.loaded && !action.refreshing,
            refreshing: thread.loaded || action.refreshing,
            loadError: "",
          },
        },
      };
    }

    case "LOAD_SUCCESS": {
      const thread = getThread(state, action.key);
      const canonicalMessages = mergeCanonicalMessages(
        thread.canonicalMessages,
        action.messages
      );

      return {
        ...state,
        threads: {
          ...state.threads,
          [action.key]: {
            ...thread,
            canonicalMessages,
            optimisticMessages: removeConfirmedOptimistic(
              thread.optimisticMessages,
              canonicalMessages
            ),
            loaded: true,
            loading: false,
            refreshing: false,
            loadError: "",
            hasMore: !!action.hasMore,
            nextCursor: action.nextCursor || null,
          },
        },
      };
    }

    case "LOAD_ERROR": {
      const thread = getThread(state, action.key);
      return {
        ...state,
        threads: {
          ...state.threads,
          [action.key]: {
            ...thread,
            loaded: thread.loaded,
            loading: false,
            refreshing: false,
            loadError: action.error || "Unable to load messages.",
          },
        },
      };
    }

    case "ADD_OPTIMISTIC": {
      const thread = getThread(state, action.key);
      return {
        ...state,
        threads: {
          ...state.threads,
          [action.key]: {
            ...thread,
            optimisticMessages: [
              ...(thread.optimisticMessages || []),
              action.message,
            ],
          },
        },
      };
    }

    case "MARK_SENDING": {
      const thread = getThread(state, action.key);
      return {
        ...state,
        threads: {
          ...state.threads,
          [action.key]: {
            ...thread,
            optimisticMessages: (thread.optimisticMessages || []).map(
              (message) =>
                message.localId === action.localId
                  ? {
                      ...message,
                      status: "sending",
                      error: "",
                      retryable: true,
                    }
                  : message
            ),
          },
        },
      };
    }

    case "MERGE_CANONICAL": {
      const thread = getThread(state, action.key);
      const canonicalMessages = mergeCanonicalMessages(
        thread.canonicalMessages,
        [action.message]
      );

      return {
        ...state,
        threads: {
          ...state.threads,
          [action.key]: {
            ...thread,
            canonicalMessages,
            optimisticMessages: removeConfirmedOptimistic(
              thread.optimisticMessages,
              canonicalMessages
            ),
            loaded: thread.loaded,
          },
        },
      };
    }

    case "MARK_FAILED": {
      const thread = getThread(state, action.key);
      const confirmed = (thread.canonicalMessages || []).some(
        (message) => optimisticMatchKey(message) === action.matchKey
      );

      if (confirmed) return state;

      return {
        ...state,
        threads: {
          ...state.threads,
          [action.key]: {
            ...thread,
            optimisticMessages: (thread.optimisticMessages || []).map(
              (message) =>
                message.localId === action.localId
                  ? {
                      ...message,
                      status: "failed",
                      error: action.error || "Unable to send note.",
                      retryable: !!action.retryable,
                    }
                  : message
            ),
          },
        },
      };
    }

    default:
      return state;
  }
}

function createClientMessageId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `cm_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

async function readResponseBody(res) {
  const contentType = safeString(res.headers?.get?.("content-type"));
  if (res.status === 204) return {};

  if (contentType.includes("application/json")) {
    return await res.json().catch(() => ({}));
  }

  const text = await res.text().catch(() => "");
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

function friendlySendError(error) {
  if (error?.status === 409 && error?.data?.code === "CLIENT_MESSAGE_ID_REUSE") {
    return {
      text: "Couldn\u2019t retry that note. Send it again as a new note.",
      retryable: false,
    };
  }

  return {
    text: "Unable to send note. Please try again.",
    retryable: true,
  };
}

export function ROMessagesProvider({ children }) {
  const auth = useAuth();
  const realtime = useRealtime();
  const [state, dispatch] = useReducer(reducer, { threads: {} });

  const generationRef = useRef(0);
  const storeIdRef = useRef("");
  const stateRef = useRef({ threads: {} });
  const optimisticLookupRef = useRef(new Map());

  const token = safeString(auth?.token).trim();
  const storeId = safeString(auth?.effectiveStoreId || auth?.activeStoreId).trim();
  const hydrated = auth?.hydrated !== false;
  const isAuthenticated = !!auth?.isAuthenticated;
  const isStoreMode = auth?.mode !== "platform";
  const isImpersonating = !!auth?.isImpersonating || !!auth?.user?.isImpersonating;
  const apiUrl = auth?.apiUrl;
  const signOut = auth?.signOut;
  const subscribeToRoMessageCreated = realtime?.subscribeToRoMessageCreated;
  const subscribeToRoMessageRetracted = realtime?.subscribeToRoMessageRetracted;
  const isReady =
    hydrated &&
    isAuthenticated &&
    isStoreMode &&
    !!token &&
    !!storeId &&
    typeof apiUrl === "function";

  useLayoutEffect(() => {
    generationRef.current += 1;
    storeIdRef.current = storeId;
    optimisticLookupRef.current.clear();
    dispatch({ type: "RESET" });
  }, [isReady, storeId, token]);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const isCurrentSession = useCallback((requestStoreId, generation) => {
    return (
      safeString(requestStoreId).trim() === storeIdRef.current &&
      generation === generationRef.current
    );
  }, []);

  const requestJson = useCallback(
    async (method, path, body) => {
      const url = apiUrl(path);
      const headers = {
        Authorization: `Bearer ${token}`,
        "X-Store-Id": storeId,
      };

      if (body != null) headers["Content-Type"] = "application/json";

      const res = await fetch(url, {
        method,
        headers,
        body: body != null ? JSON.stringify(body) : undefined,
      });

      const data = await readResponseBody(res);

      if (!res.ok) {
        const error = new Error(
          safeString(data?.error || data?.message) ||
            `${method} failed (${res.status})`
        );
        error.status = res.status;
        error.data = data;
        throw error;
      }

      return data;
    },
    [apiUrl, storeId, token]
  );

  const handleCurrentSessionAuthError = useCallback(
    (error, requestStoreId, generation) => {
      if (
        error?.status === 401 &&
        isCurrentSession(requestStoreId, generation) &&
        typeof signOut === "function"
      ) {
        signOut();
      }
    },
    [isCurrentSession, signOut]
  );

  const confirmLocalIntent = useCallback((messageValue) => {
    const message = normalizeServerMessage(messageValue);
    if (!isValidServerMessage(message)) return;

    for (const [localId, intent] of optimisticLookupRef.current.entries()) {
      if (!localIntentMatchesCanonical(intent, message)) continue;

      optimisticLookupRef.current.delete(localId);

      if (typeof intent.onConfirmed === "function") {
        try {
          intent.onConfirmed(message);
        } catch (error) {
          console.error("Message confirmation callback failed.", error);
        }
      }

      return;
    }
  }, []);

  const fetchMessages = useCallback(
    async ({ roId, before = "" } = {}) => {
      const cleanRoId = safeString(roId).trim();
      if (!isReady || !cleanRoId) return null;

      const requestStoreId = storeId;
      const generation = generationRef.current;
      const key = threadKey(requestStoreId, cleanRoId);
      const query = new URLSearchParams({ limit: String(INITIAL_LIMIT) });

      if (before) query.set("before", before);

      dispatch({ type: "LOAD_START", key, refreshing: !!before });

      try {
        const data = await requestJson(
          "GET",
          `/api/ros/${encodeURIComponent(cleanRoId)}/messages?${query.toString()}`
        );

        if (!isCurrentSession(requestStoreId, generation)) return data;

        const normalizedMessages = Array.isArray(data?.messages)
          ? data.messages.map(normalizeServerMessage)
          : [];

        normalizedMessages.forEach((message) => {
          confirmLocalIntent(message);
        });

        dispatch({
          type: "LOAD_SUCCESS",
          key,
          messages: normalizedMessages,
          hasMore: !!data?.hasMore,
          nextCursor: safeString(data?.nextCursor).trim() || null,
        });

        return data;
      } catch (error) {
        handleCurrentSessionAuthError(error, requestStoreId, generation);

        if (isCurrentSession(requestStoreId, generation)) {
          dispatch({
            type: "LOAD_ERROR",
            key,
            error: "Unable to load messages.",
          });
        }
        throw error;
      }
    },
    [
      confirmLocalIntent,
      handleCurrentSessionAuthError,
      isCurrentSession,
      isReady,
      requestJson,
      storeId,
    ]
  );

  const ensureMessages = useCallback(
    (roId) => {
      const cleanRoId = safeString(roId).trim();
      if (!isReady || !cleanRoId) return Promise.resolve(null);

      const key = threadKey(storeId, cleanRoId);
      const thread = getThread(stateRef.current, key);

      if (thread.loading || thread.refreshing) return Promise.resolve(null);
      return fetchMessages({ roId: cleanRoId });
    },
    [fetchMessages, isReady, storeId]
  );

  const refreshMessages = useCallback(
    (roId) => fetchMessages({ roId }),
    [fetchMessages]
  );

  const loadOlderMessages = useCallback(
    (roId) => {
      const cleanRoId = safeString(roId).trim();
      if (!isReady || !cleanRoId) return Promise.resolve(null);

      const thread = getThread(stateRef.current, threadKey(storeId, cleanRoId));
      if (
        !thread.hasMore ||
        !thread.nextCursor ||
        thread.loading ||
        thread.refreshing
      ) {
        return Promise.resolve(null);
      }

      return fetchMessages({ roId: cleanRoId, before: thread.nextCursor });
    },
    [fetchMessages, isReady, storeId]
  );

  const postMessage = useCallback(
    async ({ roId, body, clientMessageId }) => {
      return requestJson("POST", `/api/ros/${encodeURIComponent(roId)}/messages`, {
        body,
        clientMessageId,
      });
    },
    [requestJson]
  );

  const retractMessage = useCallback(
    async (roId, messageId) => {
      const cleanRoId = safeString(roId).trim();
      const cleanMessageId = safeString(messageId).trim();

      if (!isReady || !cleanRoId || !cleanMessageId) {
        throw new Error("Messages are offline.");
      }
      if (isImpersonating) throw new Error("This thread is read-only.");

      const data = await requestJson(
        "POST",
        `/api/ros/${encodeURIComponent(cleanRoId)}/messages/${encodeURIComponent(
          cleanMessageId
        )}/retract`
      );

      const message = normalizeServerMessage(data?.message);
      if (isValidServerMessage(message)) {
        dispatch({
          type: "MERGE_CANONICAL",
          key: threadKey(storeId, cleanRoId),
          message,
        });
      }

      return data;
    },
    [isImpersonating, isReady, requestJson, storeId]
  );

  const startPostLifecycle = useCallback(
    ({
      key,
      requestStoreId,
      generation,
      optimistic,
      matchKey,
      roId,
      body,
      clientMessageId,
    }) => {
      Promise.resolve()
        .then(() =>
          postMessage({
            roId,
            body,
            clientMessageId,
          })
        )
        .then((data) => {
          const message = normalizeServerMessage(data?.message);

          if (
            isCurrentSession(requestStoreId, generation) &&
            isValidServerMessage(message)
          ) {
            confirmLocalIntent(message);
            dispatch({ type: "MERGE_CANONICAL", key, message });
          }
        })
        .catch((error) => {
          handleCurrentSessionAuthError(error, requestStoreId, generation);

          const friendly = friendlySendError(error);

          if (isCurrentSession(requestStoreId, generation)) {
            dispatch({
              type: "MARK_FAILED",
              key,
              localId: optimistic.localId,
              matchKey: matchKey || optimisticMatchKey(optimistic),
              error: friendly.text,
              retryable: friendly.retryable,
            });
          }
        });
    },
    [confirmLocalIntent, handleCurrentSessionAuthError, isCurrentSession, postMessage]
  );

  const sendMessage = useCallback(
    (roId, bodyValue, options = {}) => {
      const cleanRoId = safeString(roId).trim();
      const body = safeString(bodyValue).trim();

      if (!isReady || !cleanRoId) throw new Error("Messages are offline.");
      if (isImpersonating) throw new Error("This thread is read-only.");
      if (!body) throw new Error("Enter a note before sending.");
      if (body.length > MAX_BODY_LENGTH) {
        throw new Error("Notes must be 4000 characters or fewer.");
      }

      const requestStoreId = storeId;
      const generation = generationRef.current;
      const key = threadKey(requestStoreId, cleanRoId);
      const clientMessageId = createClientMessageId();
      const user = auth?.user || {};
      const membership = auth?.membership || {};
      const optimistic = {
        localId: `local_${clientMessageId}`,
        id: "",
        storeId: requestStoreId,
        roId: cleanRoId,
        authorUserId: safeString(user.id || user.userId).trim(),
        authorDisplayName: safeString(user.displayName || user.name || "User").trim(),
        authorRole: safeString(
          membership.role || user.storeRole || user.role || "user"
        ).trim(),
        body,
        clientMessageId,
        localCreatedAt: nowIso(),
        createdAt: "",
        retractedAt: null,
        isRetracted: false,
        status: "sending",
        error: "",
        retryable: true,
      };

      optimisticLookupRef.current.set(optimistic.localId, {
        key,
        storeId: requestStoreId,
        roId: cleanRoId,
        body,
        clientMessageId,
        authorUserId: optimistic.authorUserId,
        matchKey: optimisticMatchKey(optimistic),
        onConfirmed:
          options && typeof options.onConfirmed === "function"
            ? options.onConfirmed
            : null,
      });

      dispatch({ type: "ADD_OPTIMISTIC", key, message: optimistic });

      startPostLifecycle({
        key,
        requestStoreId,
        generation,
        optimistic,
        matchKey: optimisticMatchKey(optimistic),
        roId: cleanRoId,
        body,
        clientMessageId,
      });

      return {
        localId: optimistic.localId,
        clientMessageId,
        storeId: requestStoreId,
        roId: cleanRoId,
        authorUserId: optimistic.authorUserId,
      };
    },
    [
      auth?.membership,
      auth?.user,
      isImpersonating,
      isReady,
      startPostLifecycle,
      storeId,
    ]
  );

  const retryMessage = useCallback(
    (roId, localId) => {
      const cleanRoId = safeString(roId).trim();
      const cleanLocalId = safeString(localId).trim();
      const lookup = optimisticLookupRef.current.get(cleanLocalId);

      if (!isReady || !cleanRoId || !lookup) return null;
      if (isImpersonating) throw new Error("This thread is read-only.");
      if (lookup.roId !== cleanRoId || lookup.storeId !== storeId) return null;

      const generation = generationRef.current;

      dispatch({ type: "MARK_SENDING", key: lookup.key, localId: cleanLocalId });

      startPostLifecycle({
        key: lookup.key,
        requestStoreId: lookup.storeId,
        generation,
        optimistic: {
          localId: cleanLocalId,
          clientMessageId: lookup.clientMessageId,
        },
        matchKey: lookup.matchKey,
        roId: lookup.roId,
        body: lookup.body,
        clientMessageId: lookup.clientMessageId,
      });

      return {
        localId: cleanLocalId,
        clientMessageId: lookup.clientMessageId,
        storeId: lookup.storeId,
        roId: lookup.roId,
        authorUserId: lookup.authorUserId,
      };
    },
    [isImpersonating, isReady, startPostLifecycle, storeId]
  );

  useEffect(() => {
    if (!isReady || typeof subscribeToRoMessageCreated !== "function") {
      return undefined;
    }

    return subscribeToRoMessageCreated((payload) => {
      if (!payload || typeof payload !== "object") return;

      const payloadStoreId = safeString(payload.storeId).trim();
      const roId = safeString(payload.roId).trim();
      const message = normalizeServerMessage(payload.message);

      if (!payloadStoreId || !roId || !isValidServerMessage(message)) return;
      if (payloadStoreId !== storeIdRef.current) return;
      if (message.storeId !== payloadStoreId || message.roId !== roId) return;

      confirmLocalIntent(message);

      dispatch({
        type: "MERGE_CANONICAL",
        key: threadKey(payloadStoreId, roId),
        message,
      });
    });
  }, [confirmLocalIntent, isReady, subscribeToRoMessageCreated]);

  useEffect(() => {
    if (!isReady || typeof subscribeToRoMessageRetracted !== "function") {
      return undefined;
    }

    return subscribeToRoMessageRetracted((payload) => {
      if (!payload || typeof payload !== "object") return;

      const payloadStoreId = safeString(payload.storeId).trim();
      const roId = safeString(payload.roId).trim();
      const message = normalizeServerMessage(payload.message);

      if (!payloadStoreId || !roId || !isValidServerMessage(message)) return;
      if (payloadStoreId !== storeIdRef.current) return;
      if (message.storeId !== payloadStoreId || message.roId !== roId) return;

      dispatch({
        type: "MERGE_CANONICAL",
        key: threadKey(payloadStoreId, roId),
        message: {
          ...message,
          body: "",
          isRetracted: true,
        },
      });
    });
  }, [isReady, subscribeToRoMessageRetracted]);

  const getThreadState = useCallback(
    (roId) => {
      const key = threadKey(storeId, roId);
      const thread = getThread(state, key);

      return {
        ...thread,
        messages: visibleMessages(thread),
      };
    },
    [state, storeId]
  );

  const value = useMemo(
    () => ({
      getThreadState,
      ensureMessages,
      refreshMessages,
      loadOlderMessages,
      sendMessage,
      retryMessage,
      retractMessage,
      canSendMessages: isReady && !isImpersonating,
      isReadOnly: isImpersonating,
      storeId,
      maxBodyLength: MAX_BODY_LENGTH,
    }),
    [
      ensureMessages,
      getThreadState,
      isImpersonating,
      isReady,
      loadOlderMessages,
      refreshMessages,
      retryMessage,
      retractMessage,
      sendMessage,
      storeId,
    ]
  );

  return (
    <ROMessagesContext.Provider value={value}>
      {children}
    </ROMessagesContext.Provider>
  );
}

export function useROMessages() {
  return (
    useContext(ROMessagesContext) || {
      getThreadState: () => EMPTY_THREAD,
      ensureMessages: () => Promise.resolve(null),
      refreshMessages: () => Promise.resolve(null),
      loadOlderMessages: () => Promise.resolve(null),
      sendMessage: () => null,
      retryMessage: () => null,
      retractMessage: () => Promise.resolve(null),
      canSendMessages: false,
      isReadOnly: false,
      storeId: "",
      maxBodyLength: MAX_BODY_LENGTH,
    }
  );
}
